import { Frustum, Object3D, Vector3 } from 'three';

import { isDefined } from '../../../../utils/variables';
import { Cache } from '../../../cache/cache-manager';
import { SkyviewCamera } from '../../../cameras/types';
import { TileNode, TileSet } from './types';
import {
  createTileNodeDataUri,
  evaluateTileNodeForCulling,
  isTileSetNode,
  recalculateCameraHeight,
} from './utilities';

enum TileNodeCullResponseType {
  Added,
  OutsideFrustum,
  NotCached,
}

type View = { camera: SkyviewCamera; offset: Vector3; worldPosition: Vector3; frustum: Frustum };

interface TileNodeCullResponse {
  type: TileNodeCullResponseType;
  cachedChunks: Object3D[];
}

function isPathWithinViewAndLoaded(
  childNode: TileNode,
  cache: Cache,
  view: View,
  parentDataExists: boolean,
): { isPathLoaded: boolean; isWithinView: boolean } {
  const { withinRange, withinFrustum } = evaluateTileNodeForCulling(
    view.worldPosition,
    view.frustum,
    childNode,
    view.offset,
  );
  const isWithinView = (!parentDataExists || withinRange) && withinFrustum;

  if (!childNode.content?.uri) {
    /**
     * If there are more child nodes it means this is a leaf node.
     * If the leaf node doesnt have data we will consider it "loaded" only
     * if no parent data also exists. Otherwise it would result in holes.
     */
    if (childNode.children.length === 0) {
      return { isWithinView, isPathLoaded: !parentDataExists };
    }

    const results = childNode.children.map((x) =>
      isPathWithinViewAndLoaded(x, cache, view, parentDataExists),
    );
    return {
      isWithinView: results.some((x) => x.isWithinView),
      isPathLoaded: results.every((x) => x.isPathLoaded),
    };
  }

  // If the node is not within view. We may consider it to be loaded.
  if (!withinFrustum) {
    return { isWithinView, isPathLoaded: true };
  }

  const childNodeUrl = createTileNodeDataUri(childNode, childNode.content.uri);

  if (!cache.isCached(childNodeUrl)) {
    return { isWithinView, isPathLoaded: false };
  }

  if (isTileSetNode(childNode)) {
    // If the cached node is a tileset, check that its root node is cached.
    const tileSetChild = cache.get(childNodeUrl) as TileSet;
    return isPathWithinViewAndLoaded(tileSetChild.root, cache, view, parentDataExists);
  }

  return { isWithinView, isPathLoaded: true };
}

/**
 * Determines what nodes to render based on a few criteria:
 *
 * The function will return the max LOD-level available within the cameras range and frustum.
 * It should never render any holes so if 5 out of 6 chunks are loaded it will render
 * the parent data instead of the child data, unless that 6th chunk is outside of the cameras frustum.
 *
 * The only time holes may be rendered is when parent data does not exist
 * and no child data is available or loaded either. In that scenario we will consider
 * the path loaded and display the neighbouring anyway.
 */
export function cullChunksToRender(
  tileNode: TileNode,
  index: number,
  cache: Cache,
  view: View,
): TileNodeCullResponse {
  const tileNodeUrl = tileNode.content?.uri
    ? createTileNodeDataUri(tileNode, tileNode.content.uri)
    : undefined;

  if (tileNodeUrl) {
    view.worldPosition.setZ(recalculateCameraHeight(view.camera, tileNode, view.offset));
  }

  /**
   * A child is to be rendered if it is within view.
   */
  let isAChildToBeRendered = false;

  /**
   * Checks that all visible paths have cached data.
   * For example if a child not does not have data but all of it's children
   * have data that is cached that "path" can be considered cached.
   *
   * If a path is outside of the camera view, we do not care wether it is cached or not.
   */
  let areAllVisiblePathsCached = true;

  for (const childNode of tileNode.children || []) {
    const { isWithinView, isPathLoaded } = isPathWithinViewAndLoaded(
      childNode,
      cache,
      view,
      isDefined(tileNodeUrl),
    );
    isAChildToBeRendered ||= isWithinView;
    areAllVisiblePathsCached &&= isPathLoaded;

    if (!areAllVisiblePathsCached) {
      break;
    }
  }

  /**
   *  If all children are loaded and any child is to be rendered,
   *  traverse all children.
   */
  if (isAChildToBeRendered && areAllVisiblePathsCached) {
    const childResponses: Object3D[] = [];
    for (const childNode of tileNode.children || []) {
      if (isTileSetNode(childNode)) {
        const uri = createTileNodeDataUri(childNode, childNode.content?.uri as unknown as string);

        if (cache.isCached(uri)) {
          /**
           *  If all children are within the frustum and this is a tileset node,
           *  traverse the root node.
           */
          const newTileSet = cache.get(uri) as TileSet;
          childResponses.push(
            ...cullChunksToRender(newTileSet.root, index + 1, cache, view).cachedChunks,
          );
        }
      } else {
        childResponses.push(...cullChunksToRender(childNode, index + 1, cache, view).cachedChunks);
      }
    }

    return {
      type: TileNodeCullResponseType.Added,
      cachedChunks: childResponses,
    };
  }

  if (tileNodeUrl && cache.isCached(tileNodeUrl)) {
    // If no child is within camera view or not all children are cached,
    // returns this node as render chunk.
    return {
      type: TileNodeCullResponseType.Added,
      cachedChunks: [cache.get(tileNodeUrl)],
    };
  } else {
    return {
      type: TileNodeCullResponseType.Added,
      cachedChunks: [],
    };
  }
}
